"use client";

import { useQuery } from "convex/react";
import { api } from "@/convex/_generated/api";
import { useRole } from "@/hooks/useRole";
import { useAuth } from "@/hooks/useAuth";
import { useEffect } from "react";
import { useRouter, usePathname } from "next/navigation";

interface MaintenanceGuardProps {
  children: React.ReactNode;
}

export function MaintenanceGuard({ children }: MaintenanceGuardProps) {
  const settings = useQuery(api.maintenanceMode.getMaintenanceMode);
  const { isAdmin } = useRole();
  const { isLoading } = useAuth();
  const router = useRouter();
  const pathname = usePathname();

  // Don't redirect if we're on maintenance pages or auth pages
  const isMaintenancePage = pathname === "/coming-soon" || pathname === "/maintenance";
  const isAuthPage = pathname.startsWith("/sign-in") ||
                     pathname.startsWith("/sign-up") ||
                     pathname.startsWith("/callback") ||
                     pathname.startsWith("/auth");
  const isApiRoute = pathname.startsWith("/api");

  useEffect(() => {
    // Don't redirect if user data or settings haven't loaded
    if (!settings || isLoading) {
      return;
    }

    if (isMaintenancePage || isAuthPage || isApiRoute) {
      return;
    }

    // Admins bypass all maintenance checks
    if (isAdmin) {
      return;
    }

    // Redirect non-admins to appropriate maintenance page
    if (settings.comingSoonEnabled) {
      router.push("/coming-soon");
    } else if (settings.maintenanceEnabled) {
      router.push("/maintenance");
    }
  }, [settings, isAdmin, isLoading, router, pathname, isMaintenancePage, isAuthPage, isApiRoute]);

  // While loading settings or auth, show nothing on protected pages
  if ((!settings || isLoading) && !isMaintenancePage && !isAuthPage) {
    return null;
  }

  // If maintenance mode is active and user is not admin, show nothing while redirecting
  if (settings && !isAdmin && !isMaintenancePage && !isAuthPage && !isApiRoute) {
    if (settings.comingSoonEnabled || settings.maintenanceEnabled) {
      return null;
    }
  }

  return <>{children}</>;
}
